import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Menu,
  Home,
  CalendarRange,
  CalendarPlus,
  Music,
  Users,
  UserCircle,
  Settings,
  LayoutDashboard,
  BarChart3,
  MapPin,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Bell,
  User,
  LogOut,
  Bed,
  Briefcase,
  Clock,
  ListMusic,
  ListOrdered,
  Mic,
  Navigation,
  Radio,
  GraduationCap,
  Wrench,
  Eye,
  EyeOff,
  Shield,
  BookOpen,
  Sparkles,
  Route,
} from 'lucide-react';
import { useUIPreferences } from '@/context/UIPreferencesContext';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import { MobileNav, MobileHeader } from '@/components/navigation';
import { MiniCalendar } from '@/components/dashboard/MiniCalendar';
import SongDetailsWidget from '@/components/ui/SongDetailsWidget';

interface ClassicLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href?: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children?: NavigationSubItem[];
}

interface NavigationSubItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const SCROLL_STORAGE_KEY = 'luma-scroll-positions';

function UpcomingEventsPlaceholder({ limit }: { limit: number }) {
  void limit;
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 text-center py-4">No upcoming events</p>
    </div>
  );
}

export const ClassicLayout: React.FC<ClassicLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showPageSummary, updatePreferences } = useUIPreferences();
  const isRestoringRef = useRef(false);
  const lastPathRef = useRef(location.pathname);

  useEffect(() => {
    const path = location.pathname;

    if (path !== lastPathRef.current) {
      lastPathRef.current = path;
      return;
    }

    const restoreScrollPosition = () => {
      try {
        const stored = localStorage.getItem(SCROLL_STORAGE_KEY);
        if (stored) {
          const positions = JSON.parse(stored);
          const savedPosition = positions[path];
          if (savedPosition !== undefined) {
            isRestoringRef.current = true;
            window.scrollTo(0, savedPosition);
            requestAnimationFrame(() => {
              isRestoringRef.current = false;
            });
          }
        }
      } catch (e) {
        console.error('Failed to restore scroll position:', e);
      }
    };

    restoreScrollPosition();

    const handleBeforeUnload = () => {
      try {
        const stored = localStorage.getItem(SCROLL_STORAGE_KEY);
        const positions = stored ? JSON.parse(stored) : {};
        positions[path] = window.scrollY;
        localStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
      } catch (e) {
        console.error('Failed to save scroll position:', e);
      }
    };

    const handleScroll = () => {
      if (!isRestoringRef.current) {
        try {
          const stored = localStorage.getItem(SCROLL_STORAGE_KEY);
          const positions = stored ? JSON.parse(stored) : {};
          positions[path] = window.scrollY;
          localStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
        } catch (e) {
          console.error('Failed to save scroll position:', e);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('classicLayout_sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [rightSidebarOpen, setRightSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('classicLayout_rightSidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const [activePanel, setActivePanel] = useState<'calendar' | 'events' | 'song' | null>(null);

  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ top: number; left: number } | null>(null);
  const submenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userRole = user?.role || '';
  const userName = user?.name || '';

  // Compute initials from available user fields
  const userInitials = (() => {
    if (user?.name) return user.name.charAt(0).toUpperCase();
    if (user?.firstName && user?.lastName) return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
    if (user?.firstName) return user.firstName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return '?';
  })();

  useEffect(() => {
    const pathname = location.pathname;
    let sectionToExpand: string | null = null;

    if (pathname.startsWith('/dashboard') || pathname.startsWith('/analytics')) {
      sectionToExpand = 'dashboard';
    } else if (pathname.startsWith('/events')) {
      sectionToExpand = 'events';
    } else if (pathname.startsWith('/songs') || pathname.startsWith('/playlists')) {
      sectionToExpand = 'music';
    }

    if (sectionToExpand && !expandedSections.includes(sectionToExpand)) {
      setExpandedSections(prev => [...prev, sectionToExpand]);
    }
  }, [location.pathname]);

  const handleSignOut = async () => {
    logout();
    navigate('/');
  };

  const handleSidebarToggle = (newState: boolean) => {
    setSidebarOpen(newState);
    localStorage.setItem('classicLayout_sidebarOpen', JSON.stringify(newState));
  };

  const handleRightSidebarToggle = (newState: boolean) => {
    setRightSidebarOpen(newState);
    localStorage.setItem('classicLayout_rightSidebarOpen', JSON.stringify(newState));
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionName)
        ? prev.filter(name => name !== sectionName)
        : [...prev, sectionName]
    );
  };

  const navigation: NavigationItem[] = [
    {
      name: 'Dashboard',
      icon: LayoutDashboard,
      children: [
        { name: 'Overview', href: '/dashboard', icon: Home },
        { name: 'Analytics', href: '/analytics', icon: BarChart3 },
      ]
    },
    {
      name: 'Events',
      href: '/events',
      icon: CalendarPlus,
    },
    {
      name: 'Calendar',
      href: '/calendar',
      icon: CalendarRange,
    },
    {
      name: 'Music',
      icon: Music,
      children: [
        { name: 'Songs', href: '/songs', icon: Music },
        { name: 'Playlists', href: '/playlists', icon: ListMusic },
        { name: 'Setlists', href: '/setlists', icon: ListOrdered },
      ]
    },
    {
      name: 'Venues',
      href: '/venues',
      icon: MapPin,
    },
    {
      name: 'Accommodations',
      href: '/accommodations',
      icon: Bed,
    },
    {
      name: 'Riders',
      href: '/riders',
      icon: Briefcase,
    },
    {
      name: 'Contacts',
      href: '/contacts',
      icon: Users,
    },
    {
      name: 'Ensembles',
      href: '/ensembles',
      icon: UserCircle,
    },
    {
      name: 'Equipment',
      href: '/equipment',
      icon: Wrench,
    },
    {
      name: 'Accounting',
      href: '/accounting',
      icon: BookOpen,
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
    },
    {
      name: 'AI',
      icon: Sparkles,
      children: [
        { name: 'Tour Planning', href: '/ai/tour-planning', icon: Route },
      ]
    },
  ];

  if (userRole === 'admin') {
    navigation.push({
      name: 'Admin',
      href: '/admin',
      icon: Shield,
    });
  }

  if (isMobile) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        <MobileHeader onMenuClick={() => setMobileNavOpen(true)} />
        <MobileNav isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        <main className="flex-1 overflow-y-auto pt-14 px-4 py-6">
          {children}
        </main>
      </div>
    );
  }

  if (isTablet) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="w-48 flex-shrink-0 pt-14 bg-white border-r border-gray-200">
          <Link to="/dashboard" className="flex items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100">
            <Home className="w-5 h-5 mr-3" /> Dashboard
          </Link>
          <Link to="/events" className="flex items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100">
            <CalendarPlus className="w-5 h-5 mr-3" /> Events
          </Link>
        </div>
        <main className="flex-1 overflow-y-auto pt-14 px-4 py-6">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0 fixed top-0 left-0 right-0 z-[100]">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
              title="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </button>

            <button
              onClick={() => handleSidebarToggle(!sidebarOpen)}
              className={`hidden lg:block p-1 rounded transition-colors ${
                sidebarOpen
                  ? 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Toggle left sidebar"
            >
              <Menu className="h-4 w-4" />
            </button>

            <div>
              <h1 className="text-lg font-bold text-primary-600">Luma</h1>
              <p className="text-xs text-gray-500 -mt-1">Tour Management</p>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center">
            {location.pathname === '/dashboard' && userName && (
              <div className="text-center hidden lg:block">
                <p className="text-sm text-gray-600">
                  Welcome back, <span className="font-semibold text-gray-900">{userName}</span>
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => updatePreferences({ showPageSummary: !showPageSummary })}
              className={`p-1 rounded transition-colors ${
                showPageSummary
                  ? 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title={showPageSummary ? 'Hide page summaries' : 'Show page summaries'}
            >
              {showPageSummary ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
            </button>

            <button
              onClick={() => handleRightSidebarToggle(!rightSidebarOpen)}
              className={`hidden lg:block p-1 rounded transition-colors ${
                rightSidebarOpen
                  ? 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Toggle right sidebar"
            >
              {rightSidebarOpen ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>

            <button className="p-2 rounded-md text-gray-400 hover:text-gray-600">
              <Bell className="h-6 w-6" />
            </button>

            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100"
              >
                <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {userInitials}
                  </span>
                </div>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                  <Link
                    to="/settings/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Link>
                  <hr className="my-1" />
                  <button
                    onClick={handleSignOut}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 pt-16 overflow-y-hidden overflow-x-visible">
        <div className={`${sidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 hidden lg:flex lg:flex-col bg-white border-r border-gray-200 shadow-sm relative z-50`}>
          <div className="h-16 px-3 border-b border-gray-200 flex items-center">
            <button
              onClick={() => handleSidebarToggle(!sidebarOpen)}
              className={`p-2 rounded-md text-gray-400 hover:text-gray-600 transition-colors ${!sidebarOpen ? 'mx-auto' : ''}`}
              title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {sidebarOpen ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>
          </div>

          <nav className="flex-1 px-2 py-4 overflow-y-auto overflow-x-visible">
            <ul className="space-y-1 overflow-visible">
              {navigation.map((item) => (
                <li key={item.name}>
                  {item.children ? (
                    <div className="relative group/submenu">
                      <button
                        onClick={() => toggleSection(item.name.toLowerCase())}
                        onMouseEnter={(e) => {
                          if (!sidebarOpen) {
                            if (submenuTimeoutRef.current) {
                              clearTimeout(submenuTimeoutRef.current);
                              submenuTimeoutRef.current = null;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setSubmenuPosition({ top: rect.top, left: rect.right + 4 });
                            setActiveSubmenu(item.name);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!sidebarOpen) {
                            submenuTimeoutRef.current = setTimeout(() => {
                              setActiveSubmenu(null);
                              setSubmenuPosition(null);
                              submenuTimeoutRef.current = null;
                            }, 200);
                          }
                        }}
                        className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'} w-full px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200 relative`}
                        title={!sidebarOpen ? item.name : undefined}
                      >
                        <div className="flex items-center">
                          <item.icon className={`${sidebarOpen ? 'mr-3' : ''} h-5 w-5 text-gray-400 group-hover/submenu:text-primary-600 flex-shrink-0`} />
                          {sidebarOpen && item.name}
                        </div>
                        {sidebarOpen && (
                          expandedSections.includes(item.name.toLowerCase()) ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )
                        )}

                        {!sidebarOpen && (
                          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/submenu:opacity-100 pointer-events-none whitespace-nowrap z-[100] transition-opacity duration-75 delay-0">
                            {item.name}
                          </div>
                        )}
                      </button>

                      {sidebarOpen && expandedSections.includes(item.name.toLowerCase()) && (
                        <ul className="mt-1 ml-6 space-y-1">
                          {item.children.map((subItem) => {
                            const isActive = location.pathname === subItem.href ||
                              (subItem.href !== '/' && location.pathname.startsWith(subItem.href));
                            return (
                              <li key={subItem.name}>
                                <Link
                                  to={subItem.href}
                                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                                    isActive
                                      ? 'text-primary-700 bg-primary-100'
                                      : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                                  }`}
                                >
                                  <subItem.icon className={`mr-3 h-4 w-4 transition-colors duration-200 ${
                                    isActive
                                      ? 'text-primary-600'
                                      : 'text-gray-400 group-hover:text-primary-600'
                                  }`} />
                                  <span className="truncate">{subItem.name}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href!}
                      className={`group flex items-center ${sidebarOpen ? 'px-3' : 'px-2 justify-center'} py-2 text-sm font-medium rounded-md transition-colors duration-200 relative ${
                        location.pathname === item.href || location.pathname.startsWith(item.href!)
                          ? 'text-primary-700 bg-primary-100'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                      title={!sidebarOpen ? item.name : undefined}
                    >
                      <item.icon className={`${sidebarOpen ? 'mr-3' : ''} h-5 w-5 transition-colors duration-200 flex-shrink-0 ${
                        location.pathname === item.href || location.pathname.startsWith(item.href!)
                          ? 'text-primary-600'
                          : 'text-gray-400 group-hover:text-primary-600'
                      }`} />
                      {sidebarOpen && <span className="truncate">{item.name}</span>}

                      {!sidebarOpen && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-[100] transition-opacity duration-75 delay-0">
                          {item.name}
                        </div>
                      )}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-gray-600/75 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="flex-1 overflow-y-auto py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </main>

        <div className={`${rightSidebarOpen ? 'w-80' : 'w-16'} transition-all duration-300 hidden lg:flex lg:flex-col bg-gray-50 border-l border-gray-200 shadow-sm relative z-50`}>
          <div className="h-16 px-3 border-b border-gray-200 flex items-center">
            <button
              onClick={() => handleRightSidebarToggle(!rightSidebarOpen)}
              className={`p-2 rounded-md text-gray-400 hover:text-gray-600 transition-colors ${!rightSidebarOpen ? 'mx-auto' : ''}`}
              title={rightSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {rightSidebarOpen ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
          </div>

          {rightSidebarOpen ? (
            <div className="p-4 flex-1 overflow-y-auto space-y-6">
              <div>
                <MiniCalendar />
              </div>
              <div className="border-t border-gray-200 pt-4">
                <UpcomingEventsPlaceholder limit={5} />
              </div>
              <div className="border-t border-gray-200 pt-4">
                <SongDetailsWidget />
              </div>
            </div>
          ) : (
            <div className="p-2 flex-1 overflow-y-auto space-y-4">
              <div className="group/widget relative">
                <button
                  onMouseEnter={() => setActivePanel('calendar')}
                  className="w-full p-2 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  <CalendarRange className="h-5 w-5" />
                </button>
                <div className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/widget:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-75 delay-0">
                  Calendar
                </div>
              </div>

              <div className="group/widget relative">
                <button
                  onMouseEnter={() => setActivePanel('events')}
                  className="w-full p-2 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  <CalendarPlus className="h-5 w-5" />
                </button>
                <div className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/widget:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-75 delay-0">
                  Upcoming Events
                </div>
              </div>

              <div className="group/widget relative">
                <button
                  onMouseEnter={() => setActivePanel('song')}
                  className="w-full p-2 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  <Music className="h-5 w-5" />
                </button>
                <div className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/widget:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-75 delay-0">
                  Song Details
                </div>
              </div>
            </div>
          )}

          {!rightSidebarOpen && activePanel && (
            <div
              className="fixed right-16 top-16 bottom-0 w-80 bg-white border-l border-gray-200 shadow-2xl z-[200] transform transition-transform duration-300 ease-out"
              onMouseLeave={() => setActivePanel(null)}
            >
              <div className="h-full overflow-y-auto p-4 space-y-6">
                {activePanel === 'calendar' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <CalendarRange className="h-4 w-4 mr-2" />
                      Calendar
                    </h3>
                    <MiniCalendar />
                  </div>
                )}

                {activePanel === 'events' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Upcoming Events
                    </h3>
                    <UpcomingEventsPlaceholder limit={5} />
                  </div>
                )}

                {activePanel === 'song' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Music className="h-4 w-4 mr-2" />
                      Now Playing
                    </h3>
                    <SongDetailsWidget />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {!sidebarOpen && activeSubmenu && submenuPosition && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[220px] z-[200]"
          style={{ top: `${submenuPosition.top}px`, left: `${submenuPosition.left}px` }}
          onMouseEnter={() => {
            if (submenuTimeoutRef.current) {
              clearTimeout(submenuTimeoutRef.current);
              submenuTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            setActiveSubmenu(null);
            setSubmenuPosition(null);
          }}
        >
          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 bg-gray-50">
            {activeSubmenu}
          </div>
          <div className="py-1">
            {navigation.find(item => item.name === activeSubmenu)?.children?.map((subItem) => {
              const isActive = location.pathname === subItem.href ||
                (subItem.href !== '/' && location.pathname.startsWith(subItem.href));
              return (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  className={`flex items-center px-4 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'text-primary-700 bg-primary-50 font-medium'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                  onClick={() => {
                    setActiveSubmenu(null);
                    setSubmenuPosition(null);
                  }}
                >
                  <subItem.icon className={`mr-3 h-4 w-4 flex-shrink-0 ${
                    isActive ? 'text-primary-600' : 'text-gray-400'
                  }`} />
                  <span>{subItem.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

export default ClassicLayout;
